import { useEffect, useState, Suspense } from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Permission } from '@shawwir/contracts';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button, Badge, cn, Spinner } from '@/components/ui';
import { Icon, type IconName } from './icons';
import { CommandPalette } from './CommandPalette';
import { ThemeToggle } from './ThemeToggle';

/**
 * SHL-1..SHL-6. Replaces the vendored template chrome wholesale: no jQuery
 * collapse, no fake to-do list, no six fictional chat contacts, no
 * "BootstrapDash" copyright, and a nav where each item has its own icon and a
 * genuine active state.
 */

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconName;
  permission: Permission;
  /** Shows a live count badge, e.g. unresolved emergencies (EMG-5). */
  badge?: 'emergencies' | 'lawyerVerification';
  /**
   * Match this route exactly. Needed where a nested route exists that would
   * otherwise light up its parent too, e.g. /lawyers vs /lawyers/verification.
   */
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: 'dashboard', permission: 'analytics:read' },
  { to: '/requests', labelKey: 'nav.requests', icon: 'requests', permission: 'requests:read' },
  { to: '/appointments', labelKey: 'nav.appointments', icon: 'appointments', permission: 'appointments:read' },
  { to: '/orders', labelKey: 'nav.orders', icon: 'orders', permission: 'orders:read' },
  {
    to: '/emergencies',
    labelKey: 'nav.emergencies',
    icon: 'emergencies',
    permission: 'emergencies:read',
    badge: 'emergencies',
  },
  { to: '/lawyers', labelKey: 'nav.lawyers', icon: 'lawyers', permission: 'lawyers:read', end: true },
  {
    to: '/lawyers/verification',
    labelKey: 'nav.lawyerVerification',
    icon: 'lawyers',
    permission: 'lawyers:verify',
    badge: 'lawyerVerification',
  },
  { to: '/clients', labelKey: 'nav.clients', icon: 'clients', permission: 'clients:read' },
  { to: '/services', labelKey: 'nav.services', icon: 'services', permission: 'services:read' },
  {
    to: '/consultations',
    labelKey: 'nav.consultations',
    icon: 'consultations',
    permission: 'consultations:read',
  },
  { to: '/balances', labelKey: 'nav.balances', icon: 'balances', permission: 'balances:read' },
  { to: '/promocodes', labelKey: 'nav.promocodes', icon: 'promocodes', permission: 'promocodes:read' },
  { to: '/dependencies', labelKey: 'nav.dependencies', icon: 'taxonomies', permission: 'dependencies:read' },
  { to: '/audit', labelKey: 'nav.audit', icon: 'audit', permission: 'audit:read' },
  { to: '/settings/users', labelKey: 'nav.users', icon: 'users', permission: 'users:manage' },
];

export function AppShell() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('nav:collapsed') === '1');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('nav:collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // SHL-3: the command palette, on the shortcut people already expect.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const visibleNav = NAV.filter((item) => can(item.permission));

  return (
    <div className="flex h-full">
      {/* NFR-4: a skip link, so keyboard users are not walked through the nav
          on every page. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-brand focus:px-3 focus:py-2 focus:text-brand-fg"
      >
        Skip to content
      </a>

      <aside
        className={cn(
          'flex shrink-0 flex-col border-e border-border bg-surface transition-[width]',
          collapsed ? 'w-14' : 'w-56',
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b border-border px-3">
          <div className="grid h-7 w-7 shrink-0 place-items-center rounded bg-brand text-xs font-bold text-brand-fg">
            S
          </div>
          {!collapsed && (
            <span className="truncate text-sm font-semibold">{t('app.name')}</span>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-2" aria-label="Main">
          <ul className="flex flex-col gap-0.5">
            {visibleNav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/' || item.end === true}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
                      // SHL-1: a real active state, driven by the router.
                      isActive
                        ? 'bg-brand/10 font-medium text-brand'
                        : 'text-muted hover:bg-raised hover:text-fg',
                    )
                  }
                  title={collapsed ? t(item.labelKey) : undefined}
                >
                  <Icon name={item.icon} className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
                  {item.badge === 'emergencies' && <EmergencyBadge collapsed={collapsed} />}
                  {item.badge === 'lawyerVerification' && (
                    <PendingLawyersBadge collapsed={collapsed} />
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-border p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '»' : '«'}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <Header onOpenPalette={() => setPaletteOpen(true)} />

        <main id="main" className="flex-1 overflow-y-auto p-4">
          {/* SHL-8: a boundary per route, so one broken page is not a white
              screen for the whole app. */}
          <Suspense
            fallback={
              <div className="stack-inline justify-center p-12 text-sm text-muted">
                <Spinner />
                {t('common.loading')}
              </div>
            }
          >
            <Outlet key={location.pathname} />
          </Suspense>
        </main>

        {/* SHL-6: our own footer. */}
        <footer className="border-t border-border px-4 py-2 text-xs text-muted">
          {t('app.name')} · {t('app.tagline')}
        </footer>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

/** EMG-5: polled every 30s, badged in the nav and echoed on the dashboard. */
function EmergencyBadge({ collapsed }: { collapsed: boolean }) {
  const { can } = useAuth();
  const { data } = useQuery({
    queryKey: ['emergencies', 'unresolved-count'],
    queryFn: () => api.get<{ data: { count: number } }>('/emergencies/unresolved-count'),
    enabled: can('emergencies:read'),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const count = data?.data.count ?? 0;
  if (count === 0) return null;

  return (
    <span className={cn('ms-auto', collapsed && 'absolute end-1 top-1')}>
      <Badge tone="danger">{count}</Badge>
    </span>
  );
}

/**
 * LAW-11: how many lawyer registrations are waiting on a reviewer.
 *
 * Keyed under ['lawyers', ...] so the ['lawyers'] invalidation that every
 * lawyer mutation already performs refreshes this badge for free.
 */
function PendingLawyersBadge({ collapsed }: { collapsed: boolean }) {
  const { can } = useAuth();
  const { data } = useQuery({
    queryKey: ['lawyers', 'pending-count'],
    queryFn: () => api.get<{ data: { count: number } }>('/lawyers/pending-count'),
    enabled: can('lawyers:verify'),
    refetchInterval: 60_000,
    staleTime: 55_000,
  });

  const count = data?.data.count ?? 0;
  if (count === 0) return null;

  return (
    <span className={cn('ms-auto', collapsed && 'absolute end-1 top-1')}>
      <Badge tone="warn">{count}</Badge>
    </span>
  );
}

function Header({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // SHL-2: breadcrumbs derived from the actual route, not a static placeholder.
  const crumbs = location.pathname.split('/').filter(Boolean);

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4">
      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        <ol className="flex items-center gap-1.5 text-sm text-muted">
          <li>
            <Link to="/" className="hover:text-fg">
              {t('nav.dashboard')}
            </Link>
          </li>
          {crumbs.map((crumb, index) => (
            <li key={`${crumb}-${index}`} className="stack-inline">
              <span aria-hidden="true">/</span>
              <span className={cn('truncate', index === crumbs.length - 1 && 'text-fg')}>
                {crumb}
              </span>
            </li>
          ))}
        </ol>
      </nav>

      {/* SHL-3: a real global search, not a decorative input. */}
      <Button variant="secondary" size="sm" onClick={onOpenPalette} className="gap-2">
        <span className="text-muted">{t('common.search')}</span>
        <kbd className="rounded border border-border px-1 text-[10px] text-muted">⌘K</kbd>
      </Button>

      <ThemeToggle />

      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="stack-inline rounded-md px-2 py-1.5 text-sm hover:bg-raised"
        >
          {/* AUTH-11: the real signed-in admin, not "Welcome Nouran". */}
          <span className="grid h-7 w-7 place-items-center rounded-full bg-raised text-xs font-semibold">
            {(user?.name || user?.phone || '?').slice(0, 2).toUpperCase()}
          </span>
          <span className="hidden max-w-32 truncate sm:block">{user?.name || user?.phone}</span>
        </button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden="true" />
            <div
              role="menu"
              className="absolute end-0 z-20 mt-1 w-56 rounded-md border border-border bg-surface p-1 shadow-lg"
            >
              <div className="border-b border-border px-2 py-2">
                <p className="truncate text-xs text-muted">{t('auth.signedInAs')}</p>
                <p className="truncate text-sm font-medium">{user?.name || user?.phone}</p>
                {user && (
                  <Badge tone="brand" className="mt-1">
                    {t(`enum.role.${user.role}`)}
                  </Badge>
                )}
              </div>
              {/* AUTH-5: a working logout. The legacy one was a dead <a>. */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  void logout();
                }}
                className="w-full rounded px-2 py-1.5 text-start text-sm text-danger hover:bg-raised"
              >
                {t('auth.signOut')}
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
